import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format, addWeeks } from "date-fns";
import { 
  ArrowLeft, Save, Send, Printer, CheckCircle2, Package, Truck, 
  FileText, User, Shirt, Ruler, AlertTriangle, Clock, Check, Copy, Factory
} from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { getStyleCode } from "@/data/styleCodes";
import OrderActions from "@/components/OrderActions";

// Maps style state-field -> [human label, options array, RC code field name]
// Options arrays mirror the same lists used in MTMStylesStep / AdminMTMStylesPage
const STYLE_FIELD_META = {
  jacketStyling: {
    label: "Jacket",
    fields: {
      body_style:               ["Body Style",         ["Slim", "Regular", "Classic", "Relaxed"], "body_style"],
      front_closure:            ["Front Closure",      ["1-Button","2-Button","3-Button","3-Roll-2","4-Button","2x1 DB","4x2 DB","6x1 DB","6x2 DB","8x2 DB"], "front_closure"],
      lapel_type:               ["Lapel Type",         ["Notch","Peak","Peak Lapel","Extended Peak","Shawl"], "lapel_type"],
      lapel_width:              ["Lapel Width",        ["2.5in (6.4cm)","3in (7.6cm)","3.5in (8.9cm)","3.75in (9.5cm)","4in (10.2cm)","4.5in (11.4cm)","5in (12.7cm)","5.5in (14cm)","6in (15.2cm)"], "lapel_width"],
      gorge_line:               ["Gorge Line",         ["Standard","+1cm (0.4in)","-2cm (0.8in)","-5cm (2in)"], ""],
      waist_pocket_type:        ["Waist Pocket",       ["Welt","Flap","Patch","Double Besom","No Pocket"], "waist_pocket_type"],
      waist_pocket_angle:       ["Pocket Angle",       ["Straight","Slanted"], "waist_pocket_angle"],
      ticket_pocket:            ["Ticket Pocket",      ["None","Welt","Flap","Patch","Double Besom"], "ticket_pocket"],
      chest_pocket:             ["Chest Pocket",       ["None","Welt","Patch","Double Besom"], "chest_pocket"],
      button_style:             ["Button Style",       ["Horn","Corozo","Mother of Pearl","Metal"], ""],
      exterior_stitch:          ["Exterior Stitch",    ["None","1/16\" Pic","5/16\" Pic","1/16\" Top","5/16\" Top","1/4\" Double Top","1/4\" Double Pic"], ""],
      sleeve_buttonholes:       ["Sleeve Buttonholes", ["4 Kissing","4 Non-Kissing","3 Kissing","3 Non-Kissing","No Buttons"], ""],
      vent:                     ["Vent",               ["Single Vent","Double Vent","No Vent"], "vent"],
      lapel_buttonhole:         ["Lapel Buttonhole",   ["None","Working Buttonhole","Milanese Hole","Keyhole"], "lapel_buttonhole"],
    },
  },
  pantStyling: {
    label: "Trouser",
    fields: {
      body_style:        ["Body Style",      ["Slim","Regular","Classic","Relaxed"], "pant_body_style"],
      front_style:       ["Front Style",     ["Flat Front","Single Pleat","Double Pleat"], "pant_front_style"],
      front_pocket:      ["Front Pocket",    ["Slant","On-Seam","Western","Double Besom"], "pant_front_pocket"],
      back_pocket_style: ["Back Pocket",     ["Double Welt","Single Welt","Patch","Double Besom"], "pant_back_pocket_style"],
      back_pocket_closure:["Back Closure",   ["Button","No Button"], "pant_back_pocket_closure"],
      bottom_finishing:  ["Bottom Finish",   ["Plain Hem","Cuff 1.5\"","Cuff 1.75\"","Cuff 2\""], "pant_bottom_finishing"],
      front_closure:     ["Front Closure",   ["Button & Tab","Hook & Bar","Extended Tab"], "pant_front_closure"],
      waistband_style:   ["Waistband",       ["Standard","Extended","Side Tabs","Belt Loops"], "pant_waistband_style"],
    },
  },
  vestStyling: {
    label: "Vest",
    fields: {
      lapel:        ["Lapel",          ["None","Notch","Peak","Shawl"], "vest_lapel"],
      front_closure:["Front Closure",  ["5-Button","6-Button","4-Button","DB 6x3"], "vest_front_closure"],
      pocket_type:  ["Pocket Type",    ["Welt","Patch","Double Besom Pocket","No Pockets"], "vest_pocket_type"],
      pocket_number:["Pocket Count",   ["2 Pockets","4 Pockets"], "vest_pocket_number"],
      back:         ["Back",           ["Same Fabric","Lining","Belt Back"], "vest_back"],
    },
  },
};

const StyleChips = ({ stylingObj, meta }) => {
  if (!stylingObj || typeof stylingObj !== "object") return null;
  const rows = Object.entries(meta.fields)
    .map(([key, [label, options, prefix]]) => {
      const val = stylingObj[key];
      if (!val) return null;
      const code = getStyleCode(prefix, options, val);
      return { label, value: val, code };
    })
    .filter(Boolean);
  if (!rows.length) {
    return <p className="text-xs text-muted-foreground italic">No styles set</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {rows.map((r) => (
        <span
          key={r.label}
          className="inline-flex items-center gap-1.5 bg-muted/40 border border-white/5 rounded px-2 py-0.5 text-[10px]"
          title={r.label}
        >
          <span className="text-muted-foreground">{r.label}:</span>
          <span>{r.value}</span>
          {r.code && (
            <code className="text-amber-400/80 font-mono">{r.code}</code>
          )}
        </span>
      ))}
    </div>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GARMENT_TYPES = [
  { value: "jacket", label: "Jacket Only" },
  { value: "trouser", label: "Trouser Only" },
  { value: "vest", label: "Vest Only" },
  { value: "2piece", label: "2-Piece Suit (Jacket + Trouser)" },
  { value: "3piece", label: "3-Piece Suit (Jacket + Trouser + Vest)" },
];

const ORDER_STATUSES = [
  { id: "draft", label: "Draft", icon: FileText, color: "bg-gray-500" },
  { id: "submitted", label: "Submitted", icon: Send, color: "bg-blue-500" },
  { id: "in_production", label: "In Production", icon: Package, color: "bg-yellow-500" },
  { id: "shipped", label: "Shipped", icon: Truck, color: "bg-purple-500" },
  { id: "complete", label: "Complete", icon: CheckCircle2, color: "bg-green-500" }
];

const AdminMTMSubmitPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Order data from localStorage
  const [orderNumber, setOrderNumber] = useState("");
  const [generalInfo, setGeneralInfo] = useState({});
  const [selectedClient, setSelectedClient] = useState(null);
  const [includeJacket, setIncludeJacket] = useState(false);
  const [includeTrouser, setIncludeTrouser] = useState(false);
  const [includeVest, setIncludeVest] = useState(false);
  const [fabricSelection, setFabricSelection] = useState(null);
  const [liningSelection, setLiningSelection] = useState(null);
  const [measurements, setMeasurements] = useState({});
  const [styles, setStyles] = useState({});
  
  // Factory notes
  const [factoryNotes, setFactoryNotes] = useState("");
  const [rushOrder, setRushOrder] = useState(false);
  const [priority, setPriority] = useState("normal");
  
  // Print dialog
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  
  // Success dialog for after submission
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [submittedOrderNumber, setSubmittedOrderNumber] = useState("");
  const [submittedOrderId, setSubmittedOrderId] = useState("");

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") { navigate("/admin"); return; }
    
    loadOrderData();
  }, [navigate]);

  const loadOrderData = async () => {
    try {
      // Get next order number
      const orderRes = await axios.get(`${API}/mtm-orders/next-number`);
      setOrderNumber(orderRes.data.next_number);
      
      // Load from localStorage
      const savedGarments = localStorage.getItem("mtm_garments");
      if (savedGarments) {
        const g = JSON.parse(savedGarments);
        setIncludeJacket(g.includeJacket || false);
        setIncludeTrouser(g.includeTrouser || false);
        setIncludeVest(g.includeVest || false);
      }
      
      const savedFabric = localStorage.getItem("mtm_fabric");
      if (savedFabric) {
        const f = JSON.parse(savedFabric);
        setFabricSelection(f.fabricSelection);
        setLiningSelection(f.liningSelection);
      }
      
      const savedMeasurements = localStorage.getItem("mtm_measurements");
      if (savedMeasurements) {
        setMeasurements(JSON.parse(savedMeasurements));
      }
      
      const savedStyles = localStorage.getItem("mtm_styles");
      if (savedStyles) {
        setStyles(JSON.parse(savedStyles));
      }
      
      const savedDraft = localStorage.getItem("mtm_draft");
      if (savedDraft) {
        const d = JSON.parse(savedDraft);
        setGeneralInfo(d.generalInfo || {});
        setSelectedClient(d.selectedClient);
        setFactoryNotes(d.factoryNotes || "");
        setRushOrder(d.rushOrder || false);
        setPriority(d.priority || "normal");
      }
    } catch (error) {
      console.error("Failed to load order data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Compute garment type
  const garmentType = useMemo(() => {
    if (includeJacket && includeTrouser && includeVest) return "3piece";
    if (includeJacket && includeTrouser) return "2piece";
    if (includeJacket) return "jacket";
    if (includeTrouser) return "trouser";
    if (includeVest) return "vest";
    return "";
  }, [includeJacket, includeTrouser, includeVest]);

  const garmentLabel = GARMENT_TYPES.find(t => t.value === garmentType)?.label || "-";

  // Check if order is complete enough to submit
  const canSubmit = useMemo(() => {
    const hasGarment = includeJacket || includeTrouser || includeVest;
    const hasFabric = fabricSelection !== null;
    const hasMeasurements = Object.keys(measurements).some(k => measurements[k] !== null);
    return hasGarment && hasFabric && hasMeasurements;
  }, [includeJacket, includeTrouser, includeVest, fabricSelection, measurements]);

  // Build order data
  const buildOrderData = (status) => {
    const jacketMeasurements = measurements.jacket ? {
      tryon_size: measurements.jacket.modelSize,
      measurements: Object.keys(measurements.jacket.startingSpecs || {}).reduce((acc, key) => {
        acc[key] = (measurements.jacket.startingSpecs[key] || 0) + (measurements.jacket.adjustments?.[key] || 0);
        return acc;
      }, {}),
      starting_specs: measurements.jacket.startingSpecs,
      adjustments: measurements.jacket.adjustments,
      posture: measurements.jacket.posture,
      styling: styles.jacket?.styling || {},
      remarks: measurements.jacket.remarks,
      styling_remarks: styles.jacket?.styling_remarks
    } : null;

    const pantMeasurements = measurements.pant ? {
      tryon_size: measurements.pant.modelSize,
      model_type: measurements.pant.modelType,
      measurements: Object.keys(measurements.pant.startingSpecs || {}).reduce((acc, key) => {
        acc[key] = (measurements.pant.startingSpecs[key] || 0) + (measurements.pant.adjustments?.[key] || 0);
        return acc;
      }, {}),
      starting_specs: measurements.pant.startingSpecs,
      adjustments: measurements.pant.adjustments,
      posture: measurements.pant.posture,
      styling: styles.pant?.styling || {},
      remarks: measurements.pant.remarks,
      styling_remarks: styles.pant?.styling_remarks
    } : null;

    const vestMeasurements = measurements.vest ? {
      tryon_size: measurements.vest.modelSize,
      measurements: Object.keys(measurements.vest.startingSpecs || {}).reduce((acc, key) => {
        acc[key] = (measurements.vest.startingSpecs[key] || 0) + (measurements.vest.adjustments?.[key] || 0);
        return acc;
      }, {}),
      starting_specs: measurements.vest.startingSpecs,
      adjustments: measurements.vest.adjustments,
      posture: measurements.vest.posture,
      styling: styles.vest?.styling || {},
      remarks: measurements.vest.remarks,
      styling_remarks: styles.vest?.styling_remarks
    } : null;

    return {
      order_number: orderNumber,
      client_id: selectedClient?.id || "",
      client_name: selectedClient?.name || `${generalInfo.customerFirstName || ""} ${generalInfo.customerLastName || ""}`.trim(),
      client_email: selectedClient?.email || generalInfo.email || "",
      garment_type: garmentType,
      jacket_measurements: jacketMeasurements,
      pant_measurements: pantMeasurements,
      vest_measurements: vestMeasurements,
      fabric_selection: fabricSelection ? {
        rc_code: fabricSelection.rc_code,
        book: fabricSelection.book,
        composition: fabricSelection.composition || "",
        pattern: fabricSelection.pattern || "",
        color: fabricSelection.color || "",
        price: fabricSelection.price || 0
      } : null,
      lining_selection: liningSelection ? {
        rc_code: liningSelection.rc_code,
        book: liningSelection.book,
        is_new: liningSelection.is_new || false
      } : null,
      general_info: {
        store: generalInfo.store,
        salesperson: generalInfo.salesperson,
        order_date: generalInfo.orderDate,
        date_wanted: generalInfo.dateWanted || format(addWeeks(new Date(), 4), "yyyy-MM-dd"),
        order_type: generalInfo.orderType,
        reference: generalInfo.reference,
        customer_first_name: generalInfo.customerFirstName,
        customer_last_name: generalInfo.customerLastName,
        email: generalInfo.email,
        address1: generalInfo.address1,
        address2: generalInfo.address2,
        city: generalInfo.city,
        state: generalInfo.state,
        zip: generalInfo.zip,
        phone_day: generalInfo.phoneDay,
        phone_eve: generalInfo.phoneEve,
        cell_phone: generalInfo.cellPhone
      },
      factory_notes: factoryNotes,
      rush_order: rushOrder,
      priority: priority,
      status: status,
      order_date: format(new Date(), "yyyy-MM-dd"),
      estimated_arrival: generalInfo.dateWanted || format(addWeeks(new Date(), 4), "yyyy-MM-dd"),
    };
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const orderData = buildOrderData("draft");
      await axios.post(`${API}/mtm-orders`, orderData);
      
      // Clear localStorage
      clearLocalStorage();
      
      toast.success(`Order ${orderNumber} saved as draft!`);
      navigate("/admin/mtm-order");
    } catch (error) {
      console.error("Failed to save order:", error);
      toast.error("Failed to save order");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndSend = async () => {
    if (!canSubmit) {
      toast.error("Please complete all required sections before submitting");
      return;
    }
    
    setSending(true);
    try {
      const orderData = buildOrderData("submitted");
      const created = await axios.post(`${API}/mtm-orders`, orderData);
      const savedOrderId = created?.data?.id || "";
      
      // Send confirmation email if client email exists
      const clientEmail = orderData.client_email;
      if (clientEmail) {
        try {
          const emailBody = `Dear ${generalInfo.customerFirstName || orderData.client_name},

Thank you for your order with Laguerre. Your made-to-measure ${garmentLabel.toLowerCase()} order has been submitted to our factory.

Order Details:
Order Number: ${orderNumber}
Garment: ${garmentLabel}
${fabricSelection ? `Fabric: ${fabricSelection.rc_code}` : ''}
Estimated Completion: ${format(new Date(orderData.estimated_arrival), "MMMM d, yyyy")}

We will keep you updated on the progress of your order. If you have any questions, please don't hesitate to contact us.

Best regards,
The Laguerre Team`;

          await axios.post(`${API}/admin/send-email`, {
            recipient_email: clientEmail,
            recipient_name: generalInfo.customerFirstName || orderData.client_name,
            subject: `Laguerre - Order ${orderNumber} Submitted`,
            body: emailBody
          });
          toast.success(`Order ${orderNumber} submitted and confirmation sent to ${clientEmail}!`);
        } catch (emailError) {
          console.error("Failed to send confirmation email:", emailError);
          toast.success(`Order ${orderNumber} submitted! (Email notification failed)`);
        }
      } else {
        toast.success(`Order ${orderNumber} submitted to factory!`);
      }
      
      // Clear localStorage
      clearLocalStorage();
      
      // Show success dialog instead of navigating
      setSubmittedOrderNumber(orderNumber);
      setSubmittedOrderId(savedOrderId);
      setShowSuccessDialog(true);
    } catch (error) {
      console.error("Failed to submit order:", error);
      toast.error("Failed to submit order");
    } finally {
      setSending(false);
    }
  };

  const clearLocalStorage = () => {
    localStorage.removeItem("mtm_fabric");
    localStorage.removeItem("mtm_styles");
    localStorage.removeItem("mtm_garments");
    localStorage.removeItem("mtm_measurements");
    localStorage.removeItem("mtm_draft");
  };

  // Print order
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    
    const jacketMeasurements = measurements.jacket?.startingSpecs || {};
    const jacketAdj = measurements.jacket?.adjustments || {};
    const pantMeasurements = measurements.pant?.startingSpecs || {};
    const pantAdj = measurements.pant?.adjustments || {};
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>MTM Order - ${orderNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
          .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px; }
          .header h1 { font-size: 24px; margin-bottom: 5px; }
          .order-info { display: flex; justify-content: space-between; margin-bottom: 20px; }
          .order-info div { flex: 1; }
          .section { margin-bottom: 20px; border: 1px solid #ccc; }
          .section-title { background: #333; color: white; padding: 8px; font-weight: bold; }
          .section-content { padding: 10px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
          th { background: #f5f5f5; }
          .rush { background: #ffebee; border: 2px solid #f44336; }
          .notes { background: #fff3e0; padding: 10px; margin-top: 10px; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>LAGUERRE</h1>
          <h2>Made-to-Measure Order Form</h2>
        </div>
        
        <div class="order-info ${rushOrder ? 'rush' : ''}">
          <div>
            <strong>Order #:</strong> ${orderNumber}<br>
            <strong>Date:</strong> ${generalInfo.orderDate || format(new Date(), "yyyy-MM-dd")}<br>
            <strong>Salesperson:</strong> ${generalInfo.salesperson || '-'}
          </div>
          <div>
            <strong>Customer:</strong> ${generalInfo.customerFirstName || ''} ${generalInfo.customerLastName || ''}<br>
            <strong>Email:</strong> ${generalInfo.email || '-'}<br>
            <strong>Phone:</strong> ${generalInfo.cellPhone || '-'}
          </div>
          <div>
            <strong>Garment:</strong> ${garmentLabel}<br>
            <strong>Date Wanted:</strong> ${generalInfo.dateWanted || format(addWeeks(new Date(), 4), "MMM d, yyyy")}<br>
            ${rushOrder ? '<strong style="color:red">⚠️ RUSH ORDER</strong>' : ''}
          </div>
        </div>
        
        ${fabricSelection ? `
        <div class="section">
          <div class="section-title">FABRIC & LINING</div>
          <div class="section-content">
            <strong>Fabric:</strong> ${fabricSelection.rc_code} - ${fabricSelection.composition || ''} ${fabricSelection.color || ''}<br>
            <strong>Lining:</strong> ${liningSelection?.rc_code || 'Not selected'}
          </div>
        </div>
        ` : ''}
        
        ${includeJacket && measurements.jacket ? `
        <div class="section">
          <div class="section-title">JACKET MEASUREMENTS (Size: ${measurements.jacket.modelSize || '-'})</div>
          <div class="section-content">
            <table>
              <tr><th>Measurement</th><th>Starting</th><th>Adjust</th><th>Final</th></tr>
              <tr><td>Chest</td><td>${jacketMeasurements.chest || '-'}</td><td>${jacketAdj.chest || 0}</td><td>${(jacketMeasurements.chest || 0) + (jacketAdj.chest || 0)}</td></tr>
              <tr><td>Stomach</td><td>${jacketMeasurements.stomach || '-'}</td><td>${jacketAdj.stomach || 0}</td><td>${(jacketMeasurements.stomach || 0) + (jacketAdj.stomach || 0)}</td></tr>
              <tr><td>Shoulder</td><td>${jacketMeasurements.shoulder || '-'}</td><td>${jacketAdj.shoulder || 0}</td><td>${(jacketMeasurements.shoulder || 0) + (jacketAdj.shoulder || 0)}</td></tr>
              <tr><td>Sleeve Length</td><td>${jacketMeasurements.sleeve_length || '-'}</td><td>${jacketAdj.sleeve_length || 0}</td><td>${(jacketMeasurements.sleeve_length || 0) + (jacketAdj.sleeve_length || 0)}</td></tr>
              <tr><td>Back Length</td><td>${jacketMeasurements.back_length || '-'}</td><td>${jacketAdj.back_length || 0}</td><td>${(jacketMeasurements.back_length || 0) + (jacketAdj.back_length || 0)}</td></tr>
            </table>
            ${measurements.jacket.remarks ? `<p style="margin-top:10px"><strong>Notes:</strong> ${measurements.jacket.remarks}</p>` : ''}
          </div>
        </div>
        ` : ''}
        
        ${includeTrouser && measurements.pant ? `
        <div class="section">
          <div class="section-title">TROUSER MEASUREMENTS (Size: ${measurements.pant.modelSize || '-'})</div>
          <div class="section-content">
            <table>
              <tr><th>Measurement</th><th>Starting</th><th>Adjust</th><th>Final</th></tr>
              <tr><td>Waist</td><td>${pantMeasurements.waist || '-'}</td><td>${pantAdj.waist || 0}</td><td>${(pantMeasurements.waist || 0) + (pantAdj.waist || 0)}</td></tr>
              <tr><td>Seat</td><td>${pantMeasurements.seat || '-'}</td><td>${pantAdj.seat || 0}</td><td>${(pantMeasurements.seat || 0) + (pantAdj.seat || 0)}</td></tr>
              <tr><td>Thigh</td><td>${pantMeasurements.thigh || '-'}</td><td>${pantAdj.thigh || 0}</td><td>${(pantMeasurements.thigh || 0) + (pantAdj.thigh || 0)}</td></tr>
              <tr><td>Inseam</td><td>${pantMeasurements.pant_length || '-'}</td><td>${pantAdj.pant_length || 0}</td><td>${(pantMeasurements.pant_length || 0) + (pantAdj.pant_length || 0)}</td></tr>
            </table>
            ${measurements.pant.remarks ? `<p style="margin-top:10px"><strong>Notes:</strong> ${measurements.pant.remarks}</p>` : ''}
          </div>
        </div>
        ` : ''}
        
        ${factoryNotes ? `
        <div class="notes">
          <strong>⚙️ FACTORY NOTES:</strong><br>
          ${factoryNotes}
        </div>
        ` : ''}
        
        <div style="margin-top: 30px; border-top: 1px solid #ccc; padding-top: 10px;">
          <p><strong>Signature:</strong> _______________________  <strong>Date:</strong> _______________</p>
        </div>
      </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
    setShowPrintDialog(false);
  };

  if (loading) {
    return <div className="min-h-screen pt-20 flex items-center justify-center"><p className="text-muted-foreground">Loading...</p></div>;
  }

  return (
    <div className="min-h-screen pt-20 pb-12">
      {/* Step Navigation */}
      <section className="sticky top-16 z-50 bg-background/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-1 overflow-x-auto py-2">
            {[
              { id: 1, label: "GENERAL INFO", href: "/admin/mtm-order" },
              { id: 2, label: "FABRIC & LINING", href: "/admin/mtm-fabric" },
              { id: 3, label: "MEASUREMENTS", href: "/admin/mtm-measurements" },
              { id: 4, label: "STYLES", href: "/admin/mtm-styles" },
              { id: 5, label: "SUBMIT", href: "/admin/mtm-submit", active: true },
            ].map((step) => (
              <button
                key={step.id}
                onClick={() => navigate(step.href)}
                className={`
                  relative flex items-center gap-2 px-4 py-2 text-xs font-medium whitespace-nowrap transition-all
                  ${step.active 
                    ? "bg-primary text-primary-foreground" 
                    : "bg-green-600 text-white hover:bg-green-700"
                  }
                `}
              >
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] bg-white/30">
                  {step.active ? step.id : <Check size={12} />}
                </span>
                {step.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Print Dialog */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Printer size={20} /> Print Order Sheet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This will generate a printable factory order sheet with all measurements, styling details, and notes.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowPrintDialog(false)}>Cancel</Button>
              <Button className="flex-1" onClick={handlePrint}><Printer size={16} className="mr-2" /> Print Now</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Dialog - After Order Submission */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-500">
              <CheckCircle2 size={24} /> Order Submitted Successfully!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="text-center">
              <p className="text-lg font-mono text-primary mb-2">{submittedOrderNumber}</p>
              <p className="text-sm text-muted-foreground">
                Your order has been submitted to the factory for production.
              </p>
            </div>
            
            <div className="bg-muted/30 p-4 rounded-lg">
              <h4 className="text-sm font-medium mb-2">Share the receipt</h4>
              <OrderActions
                orderId={submittedOrderId}
                orderNumber={submittedOrderNumber}
                defaultPhone={generalInfo?.cellPhone || selectedClient?.phone || ""}
                variant="stack"
                testIdPrefix="mtm-submit-actions"
              />
            </div>

            <div className="bg-muted/30 p-4 rounded-lg">
              <h4 className="text-sm font-medium mb-2">What would you like to do next?</h4>
              <div className="space-y-2">
                <Button 
                  className="w-full bg-primary hover:bg-primary/90" 
                  onClick={() => {
                    // Clear all localStorage and start fresh
                    localStorage.removeItem("mtm_general");
                    localStorage.removeItem("mtm_fabric");
                    localStorage.removeItem("mtm_measurements");
                    localStorage.removeItem("mtm_styles");
                    localStorage.removeItem("mtm_garments");
                    setShowSuccessDialog(false);
                    navigate("/admin/mtm-order");
                  }}
                >
                  <FileText size={16} className="mr-2" /> Create New Order (Blank)
                </Button>
                <Button 
                  className="w-full bg-purple-600 hover:bg-purple-700" 
                  onClick={() => {
                    // Keep client info but clear garment details
                    const currentGeneral = JSON.parse(localStorage.getItem("mtm_general") || "{}");
                    localStorage.setItem("mtm_general", JSON.stringify({
                      customerFirstName: currentGeneral.customerFirstName,
                      customerLastName: currentGeneral.customerLastName,
                      email: currentGeneral.email,
                      cellPhone: currentGeneral.cellPhone,
                      address1: currentGeneral.address1,
                      city: currentGeneral.city,
                      state: currentGeneral.state,
                      zip: currentGeneral.zip,
                      garmentType: ""
                    }));
                    localStorage.removeItem("mtm_fabric");
                    localStorage.removeItem("mtm_measurements");
                    localStorage.removeItem("mtm_styles");
                    localStorage.removeItem("mtm_garments");
                    setShowSuccessDialog(false);
                    navigate("/admin/mtm-order");
                    toast.info("Starting new order for same client");
                  }}
                >
                  <User size={16} className="mr-2" /> New Order for Same Client
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  onClick={() => {
                    // Keep everything - copy this order
                    setShowSuccessDialog(false);
                    navigate("/admin/mtm-order");
                    toast.info("Order data retained - modify and submit as new order");
                  }}
                >
                  <Shirt size={16} className="mr-2" /> Copy This Order (Same Details)
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setShowSuccessDialog(false);
                    navigate("/admin/orders");
                  }}
                >
                  <Package size={16} className="mr-2" /> View All Orders
                </Button>
                <Button 
                  variant="ghost" 
                  className="w-full"
                  onClick={() => {
                    setShowSuccessDialog(false);
                    navigate("/admin");
                  }}
                >
                  Back to Dashboard
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <section className="py-4 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-2">
            <Button variant="ghost" onClick={() => navigate("/admin/mtm-styles")}>
              <ArrowLeft size={16} className="mr-2" /> Back to Styles
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowPrintDialog(true)}>
              <Printer size={14} className="mr-1" /> Print
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <CheckCircle2 className="text-green-500" size={24} />
            <div>
              <h1 className="font-serif text-2xl">Review & Submit</h1>
              <p className="text-xs text-muted-foreground">Step 5 of 5 • Review your order and submit</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-6">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          {/* Order Status */}
          <div className="bg-card border border-white/10 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium mb-4">Order Progress</h3>
            <div className="flex items-center justify-between">
              {ORDER_STATUSES.map((status, idx) => {
                const StatusIcon = status.icon;
                const isActive = status.id === "draft";
                
                return (
                  <div key={status.id} className="flex items-center">
                    <div className={`flex flex-col items-center`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${isActive ? status.color : 'bg-muted'}`}>
                        <StatusIcon size={18} />
                      </div>
                      <span className={`text-xs mt-1 ${isActive ? 'text-primary font-medium' : 'text-muted-foreground'}`}>{status.label}</span>
                    </div>
                    {idx < ORDER_STATUSES.length - 1 && (
                      <div className="w-12 h-0.5 bg-muted mx-2" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-card border border-white/10 rounded-lg overflow-hidden mb-6">
            <div className="bg-green-700 text-white px-4 py-3 font-medium">ORDER SUMMARY</div>
            <div className="p-4 space-y-4">
              {/* Customer Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-muted/30 p-3 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <User size={14} className="text-primary" />
                    <span className="text-xs text-muted-foreground font-medium">CUSTOMER</span>
                  </div>
                  <p className="text-sm font-medium">{generalInfo.customerFirstName} {generalInfo.customerLastName}</p>
                  <p className="text-xs text-muted-foreground">{generalInfo.email || "No email"}</p>
                  <p className="text-xs text-muted-foreground">{generalInfo.cellPhone || "No phone"}</p>
                </div>
                
                <div className="bg-muted/30 p-3 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText size={14} className="text-primary" />
                    <span className="text-xs text-muted-foreground font-medium">ORDER DETAILS</span>
                  </div>
                  <p className="text-sm font-mono text-primary">{orderNumber}</p>
                  <p className="text-xs text-muted-foreground">{garmentLabel}</p>
                  <p className="text-xs text-muted-foreground">Est: {format(addWeeks(new Date(), 4), "MMM d, yyyy")}</p>
                </div>
                
                <div className="bg-muted/30 p-3 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Shirt size={14} className="text-primary" />
                    <span className="text-xs text-muted-foreground font-medium">FABRIC</span>
                  </div>
                  {fabricSelection ? (
                    <>
                      <p className="text-sm font-mono text-primary">{fabricSelection.rc_code}</p>
                      <p className="text-xs text-muted-foreground">{fabricSelection.composition}</p>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">Not selected</p>
                  )}
                  {liningSelection && <p className="text-xs text-muted-foreground">Lining: {liningSelection.rc_code}</p>}
                </div>
              </div>

              {/* Measurements Summary */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Ruler size={14} className="text-primary" />
                  <span className="text-xs text-muted-foreground font-medium">MEASUREMENTS</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {includeJacket && measurements.jacket && (
                    <div className="bg-blue-500/10 border border-blue-500/30 p-3 rounded-lg">
                      <h5 className="text-xs font-medium text-blue-400 mb-2">Jacket ({measurements.jacket.modelSize || '-'})</h5>
                      <div className="grid grid-cols-2 gap-1 text-[10px]">
                        <span className="text-muted-foreground">Chest:</span>
                        <span>{((measurements.jacket.startingSpecs?.chest || 0) + (measurements.jacket.adjustments?.chest || 0)).toFixed(1)}"</span>
                        <span className="text-muted-foreground">Shoulder:</span>
                        <span>{((measurements.jacket.startingSpecs?.shoulder || 0) + (measurements.jacket.adjustments?.shoulder || 0)).toFixed(1)}"</span>
                        <span className="text-muted-foreground">Sleeve:</span>
                        <span>{((measurements.jacket.startingSpecs?.sleeve_length || 0) + (measurements.jacket.adjustments?.sleeve_length || 0)).toFixed(1)}"</span>
                      </div>
                    </div>
                  )}
                  
                  {includeTrouser && measurements.pant && (
                    <div className="bg-red-500/10 border border-red-500/30 p-3 rounded-lg">
                      <h5 className="text-xs font-medium text-red-400 mb-2">Trousers ({measurements.pant.modelSize || '-'})</h5>
                      <div className="grid grid-cols-2 gap-1 text-[10px]">
                        <span className="text-muted-foreground">Waist:</span>
                        <span>{((measurements.pant.startingSpecs?.waist || 0) + (measurements.pant.adjustments?.waist || 0)).toFixed(1)}"</span>
                        <span className="text-muted-foreground">Inseam:</span>
                        <span>{((measurements.pant.startingSpecs?.pant_length || 0) + (measurements.pant.adjustments?.pant_length || 0)).toFixed(1)}"</span>
                        <span className="text-muted-foreground">Rise:</span>
                        <span>{((measurements.pant.startingSpecs?.urise || 0) + (measurements.pant.adjustments?.urise || 0)).toFixed(1)}"</span>
                      </div>
                    </div>
                  )}
                  
                  {includeVest && measurements.vest && (
                    <div className="bg-purple-500/10 border border-purple-500/30 p-3 rounded-lg">
                      <h5 className="text-xs font-medium text-purple-400 mb-2">Vest ({measurements.vest.modelSize || '-'})</h5>
                      <div className="grid grid-cols-2 gap-1 text-[10px]">
                        <span className="text-muted-foreground">Chest:</span>
                        <span>{((measurements.vest.startingSpecs?.chest || 0) + (measurements.vest.adjustments?.chest || 0)).toFixed(1)}"</span>
                        <span className="text-muted-foreground">Length:</span>
                        <span>{((measurements.vest.startingSpecs?.back_length || 0) + (measurements.vest.adjustments?.back_length || 0)).toFixed(1)}"</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Styles */}
              {(styles?.jacketStyling || styles?.pantStyling || styles?.vestStyling) && (
                <div data-testid="submit-styles-summary">
                  <div className="flex items-center gap-2 mb-3">
                    <Shirt size={14} className="text-primary" />
                    <span className="text-xs text-muted-foreground font-medium">SELECTED STYLES</span>
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    {includeJacket && styles.jacketStyling && (
                      <div className="bg-blue-500/5 border border-blue-500/20 p-3 rounded-lg">
                        <h5 className="text-xs font-medium text-blue-400 mb-2">{STYLE_FIELD_META.jacketStyling.label}</h5>
                        <StyleChips stylingObj={styles.jacketStyling} meta={STYLE_FIELD_META.jacketStyling} />
                      </div>
                    )}
                    {includeTrouser && styles.pantStyling && (
                      <div className="bg-red-500/5 border border-red-500/20 p-3 rounded-lg">
                        <h5 className="text-xs font-medium text-red-400 mb-2">{STYLE_FIELD_META.pantStyling.label}</h5>
                        <StyleChips stylingObj={styles.pantStyling} meta={STYLE_FIELD_META.pantStyling} />
                      </div>
                    )}
                    {includeVest && styles.vestStyling && (
                      <div className="bg-purple-500/5 border border-purple-500/20 p-3 rounded-lg">
                        <h5 className="text-xs font-medium text-purple-400 mb-2">{STYLE_FIELD_META.vestStyling.label}</h5>
                        <StyleChips stylingObj={styles.vestStyling} meta={STYLE_FIELD_META.vestStyling} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Warnings */}
              {!canSubmit && (
                <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded-lg flex items-start gap-2">
                  <AlertTriangle size={16} className="text-amber-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-amber-400 font-medium">Missing Information</p>
                    <p className="text-xs text-muted-foreground">Please complete all required sections before submitting.</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Factory Notes */}
          <div className="bg-card border border-amber-500/30 rounded-lg overflow-hidden mb-6">
            <div className="bg-amber-600/80 text-white px-4 py-3 font-medium flex items-center gap-2">
              <Clock size={16} /> Factory Notes & Priority
            </div>
            <div className="p-4 space-y-4">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox 
                    checked={rushOrder} 
                    onCheckedChange={setRushOrder}
                    className="border-amber-500 data-[state=checked]:bg-amber-500"
                  />
                  <span className="text-sm text-amber-400 font-medium">🚨 Rush Order</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Priority:</span>
                  <Select value={priority} onValueChange={setPriority}>
                    <SelectTrigger className="w-28 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Notes for Factory</label>
                <Textarea 
                  value={factoryNotes} 
                  onChange={(e) => setFactoryNotes(e.target.value)}
                  placeholder="Special instructions, production notes..."
                  className="bg-background/50 border-amber-500/30"
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              variant="outline" 
              onClick={handleSaveDraft} 
              disabled={saving || sending}
              className="flex-1 h-12"
            >
              {saving ? "Saving..." : <><Save size={18} className="mr-2" /> Save as Draft</>}
            </Button>
            <Button 
              onClick={handleSaveAndSend} 
              disabled={!canSubmit || saving || sending}
              className="flex-1 h-12 bg-green-600 hover:bg-green-700"
            >
              {sending ? "Submitting..." : <><Send size={18} className="mr-2" /> Submit Order</>}
            </Button>
          </div>

          {/* Submit to External Factory Portals */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/rcmtm-portal")}
              className="h-11 border-amber-500/30 hover:bg-amber-500/10"
              data-testid="submit-to-rcmtm-btn"
            >
              <Factory size={16} className="mr-2 text-amber-400" /> Submit to RCMTM Portal
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin/mx-portal")}
              className="h-11 border-sky-500/30 hover:bg-sky-500/10"
              data-testid="submit-to-mx-btn"
            >
              <Factory size={16} className="mr-2 text-sky-400" /> Submit to MX MTM Portal
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center mt-3">
            Save as Draft to continue later, or Submit Order to send to factory and notify client
          </p>
        </div>
      </section>
    </div>
  );
};

export default AdminMTMSubmitPage;
